import { BiRightArrowAlt } from 'react-icons/bi';
import {
  Box,
  ButtonBase,
  Pagination,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  // useTheme,
} from '@mui/material';
import { ChangeEvent, FC, useMemo } from 'react';
import {
  useExpanded,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import ScrollBar from 'simplebar-react';

// component props interface
interface CustomTableProps {
  columnShape: object[];
  data: object[];
  rowClick?: (rowData: object) => void;
  hidePagination?: boolean;
  showFooter?: boolean;
  size?: number;
}

// styled component
const StyledPagination = styled(Pagination)(({ theme }) => ({
  '& .MuiPaginationItem-root': {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.disabled,
  },
  '& .MuiPaginationItem-page:hover': {
    borderRadius: 20,
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '& .MuiPaginationItem-page.Mui-selected': {
    borderRadius: 20,
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '& .MuiPaginationItem-previousNext': {
    margin: 10,
    borderRadius: 20,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': { backgroundColor: 'transparent' },
  },
}));

const CustomTable: FC<CustomTableProps> = (props) => {
  const { data, rowClick, showFooter, columnShape, hidePagination, size } =
    props;
  // hooks
  // const theme = useTheme();
  const tableData: any = useMemo(() => data, [data]);
  const columns: any = useMemo(() => columnShape, [columnShape]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageOptions,
    gotoPage,
    pageCount,
    state: { pageIndex, pageSize },
  }: // setPageSize,
  any = useTable(
    {
      columns,
      data: tableData,
      //@ts-ignore
      initialState: { pageSize: size ?? 5 },
    },
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );
  // handle pagination
  const handleChange = (_e: ChangeEvent<unknown>, currentPageNo: number) => {
    gotoPage(currentPageNo - 1);
  };

  return (
    <Box>
      <ScrollBar>
        <Table
          {...getTableProps()}
          sx={{
            borderSpacing: '0 1rem',
            borderCollapse: 'separate',
          }}
        >
          <TableHead>
            {headerGroups.map((headerGroup: any) => (
              <TableRow {...headerGroup.getHeaderGroupProps()} className="py-5">
                {headerGroup.headers.map((column: any) => (
                  <TableCell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    sx={{
                      paddingY: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      borderBottom: 0,
                      width: column.width,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                      '&:first-of-type': {
                        borderTopLeftRadius: '8px',
                        borderBottomLeftRadius: '8px',
                      },
                      '&:last-child': {
                        textAlign: 'center',
                        borderTopRightRadius: '8px',
                        borderBottomRightRadius: '8px',
                      },
                    }}
                  >
                    {column.render('Header')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody {...getTableBodyProps()}>
            {page.map((row: any) => {
              prepareRow(row);
              return (
                <TableRow
                  {...row.getRowProps(rowClick && rowClick(row.original))}
                  sx={{
                    backgroundColor: 'background.paper',
                    cursor: rowClick ? 'pointer' : 'unset',
                    '& td:first-of-type': {
                      // borderLeft: '1px solid',
                      borderTopLeftRadius: '8px',
                      borderBottomLeftRadius: '8px',
                      // borderColor: 'text.secondary',
                    },
                    '& td:last-of-type': {
                      textAlign: 'center',
                      // borderRight: '1px solid',
                      borderTopRightRadius: '8px',
                      borderBottomRightRadius: '8px',
                      // borderColor: 'text.secondary',
                    },
                    // '&:last-of-type .MuiTableCell-root': {
                    //   borderBottom:
                    //     theme.palette.mode === 'dark'
                    //       ? `1px solid ${theme.palette.divider} !important`
                    //       : `1px solid ${theme.palette.text.secondary} !important`,
                    // },
                  }}
                >
                  {row.cells.map((cell: any) => (
                    <TableCell
                      {...cell.getCellProps()}
                      // sx={{
                      //   fontSize: 13,
                      //   fontWeight: 500,
                      //   borderTop: '1px solid',
                      //   borderBottom: '1px solid',
                      //   borderColor: 'text.secondary',
                      // }}
                      className="!border-none"
                    >
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ScrollBar>

      {!hidePagination && (
        <Stack alignItems="flex-end" marginY={1}>
          <StyledPagination
            count={pageOptions.length}
            shape="rounded"
            onChange={handleChange}
          />
        </Stack>
      )}

      {showFooter && (
        <div className="flex justify-between gap-2">
          <h1 className="text-blue-500">
            Showing {pageIndex * pageSize + 1}-
            {pageIndex === 0 ? pageSize : pageSize * pageCount} of{' '}
            {pageCount * pageSize - 1} result
          </h1>
          <ButtonBase
            disableRipple
            sx={{
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            See All
            <BiRightArrowAlt className="ml-1 text-lg" />
          </ButtonBase>
        </div>
      )}
    </Box>
  );
};

export default CustomTable;
